import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import {
  getViolatedMandatoryRequirementsValidator,
  makeRequiredArray,
} from 'KnmErknmPackage/MainModule/common/violatedMandatoryRequirementsValidator';
import { getViolationEvidencesValidator } from 'KnmErknmPackage/MainModule/common/violationEvidencesValidator';
import { getActRemarksValidate } from 'KnmErknmPackage/MainModule/common/getActRemarksValidate';
import {
  KNO_INSPECTORS_DECISION_LABEL,
  makeSignerInInspectorsValidator,
} from 'KnmErknmPackage/MainModule/common/getSignerInInspectorsError';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';

import { IDecisionActForm } from '../interfaces/IDecisionActForm';
import { getDecisionActEventsValidate } from './getDecisionActEventsValidate';

export const getDecisionActValidate = (
  act: IDecisionActForm | undefined,
  basisDocumentDateTime: string | undefined,
  isUse4151: boolean,
  isUseEvidence: boolean,
  isRepeatValidationChecking: boolean,
  eventsDecision?: IKnmErknmFormEvent[],
) => {
  if (!isUse4151) {
    return undefined;
  }

  if (!act) {
    return undefined;
  }

  let knoInspectors:
    | string
    | (string | undefined)[]
    | { fullName: string | undefined; position: string | undefined }[]
    | { knoInspector: string | undefined }[]
    | undefined = !act.knoInspectors || act.knoInspectors?.length === 0 ? 'Поле не может быть пустым' : undefined;

  if (act.knoInspectors && act.knoInspectors?.length !== 0) {
    const errors = act.knoInspectors?.map(knoInspector => {
      return { knoInspector: makeRequired('Поле не может быть пустым')(knoInspector?.knoInspector) };
    });

    // @ts-ignore
    if (errors.filter(i => i).length) {
      knoInspectors = errors;
    } else {
      knoInspectors = undefined;
    }
  }

  let holdingDateTime: string | undefined;
  let durationDays: string | undefined;
  let durationHours: string | undefined;

  if (act.paperless) {
    holdingDateTime = composeValidators(
      makeRequired(REQUIRED_ERROR),
      makeNoSoonerThenValidator(
        'Дата и время начала проведения КНМ, СР не должны быть раньше даты начала проведения КНМ, СР',
        () => basisDocumentDateTime,
      ),
    )(act.holdingDateTime);
    durationDays = composeValidators(
      makeRequired(REQUIRED_ERROR),
      makeIsNormalInteger('Введены недопустимые символы'),
    )(act.durationDays);
    durationHours = composeValidators(
      makeRequired(REQUIRED_ERROR),
      makeIsNormalInteger('Введены недопустимые символы'),
    )(act.durationHours);
  } else if (act.holdingDateTime) {
    holdingDateTime = makeNoSoonerThenValidator(
      'Дата и время начала проведения КНМ, СР не должны быть раньше даты начала проведения КНМ, СР',
      () => basisDocumentDateTime,
      DATE_FORMAT_LONG_MINUTE_FNS,
    )(act.holdingDateTime);
    durationDays = makeIsNormalInteger('Введены недопустимые символы')(act.durationDays);
    durationHours = makeIsNormalInteger('Введены недопустимые символы')(act.durationHours);
  }

  return {
    numberAct: act.paperless ? undefined : makeRequired(REQUIRED_ERROR)(act?.numberAct),
    creationActDateTime: act.paperless
      ? undefined
      : makeRequired(REQUIRED_ERROR)(act?.creationActDateTime) ||
        makeNoSoonerThenValidator(
          'Дата и время составления акта не может быть раньше даты документа, на основании которого проводится мероприятие',
          () =>
            formatDate({
              date: basisDocumentDateTime || '',
              dateFormat: DATE_FORMAT_ISO_FNS,
              parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            }),
          DATE_FORMAT_LONG_MINUTE_FNS,
        )(
          formatDate({
            date: act?.creationActDateTime || '',
            dateFormat: DATE_FORMAT_ISO_FNS,
            parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
          }),
        ),
    document: {
      attachments: !act.paperless ? makeRequired(REQUIRED_ERROR)(act?.document?.attachments) : undefined,
    },
    knoInspectors: !act.paperless ? undefined : knoInspectors,
    holdingDateTime,
    durationDays,
    durationHours,
    isViolatedMandatoryRequirements: !act.paperless
      ? undefined
      : makeRequired('Поле не может быть пустым')(act.isViolatedMandatoryRequirements),
    violatedMandatoryRequirements:
      act.paperless && act.isViolatedMandatoryRequirements?.value === true
        ? composeValidators(
            makeRequiredArray('Поле не может быть пустым'),
            getViolatedMandatoryRequirementsValidator({
              isSigned: act.signed,
              isValidateRequiredViolatedMandatory: true,
              isDecisionMode: false,
              isProdReady: true,
              isRepeatValidationChecking,
            }),
          )(act.violatedMandatoryRequirements)
        : undefined,
    isViolationEvidenceExists:
      act.paperless && isUseEvidence
        ? makeRequired('Поле не может быть пустым')(act.isViolationEvidenceExists)
        : undefined,
    violationEvidences:
      act.paperless && isUseEvidence && act.isViolationEvidenceExists?.value === true
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            getViolationEvidencesValidator(act.isViolationEvidenceExists.value, false, act.holdingDateTime),
          )(act.violationEvidences)
        : undefined,
    fioAndPositionSinger:
      act.paperless && !act.systemSignatureUsed
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeSignerInInspectorsValidator(act.knoInspectors, KNO_INSPECTORS_DECISION_LABEL),
          )(act.fioAndPositionSinger?.value.fullName)
        : undefined,
    involvedWitnesses:
      act.paperless && act.involvedWitnesses?.length
        ? act.involvedWitnesses.map(witnesses => ({
            fullName: makeRequired(REQUIRED_ERROR)(witnesses.fullName),
          }))
        : undefined,
    familiarizationNotifications: act?.familiarizationNotifications?.map(fn => ({
      knmNoticeType: makeRequired(REQUIRED_ERROR)(fn?.knmNoticeType),
      noticeDate: makeRequired(REQUIRED_ERROR)(fn?.noticeDate),
    })),
    remarks: getActRemarksValidate(act?.remarks),
    events: getDecisionActEventsValidate({
      events: act?.events,
      holdingDateTime: act?.holdingDateTime,
      isInteractionCategoryOrInspectorRequired: true,
      eventsDecision,
    }),
  };
};
